import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { SimDetailsAccordion } from "@/components/Portal/Cockpit/DeviceManagement/Tariff/TariffDisplay/SimDetailsAccordion";
import { SimPricingDetails } from "@/components/Portal/Cockpit/DeviceManagement/Tariff/TariffDisplay/SimPricingDetails";
import { TariffHeader } from "@/components/Portal/Cockpit/DeviceManagement/Tariff/TariffHeader/TariffHeader";
import { SimCardDetail } from "@/services/model";
import { getBaseFlatName, getTariffType } from "@/utils/deviceUtils";
import {
  calculateDiscounts,
  getIncludedFeaturesCostDetail,
  getNextPaymentDate,
} from "@/utils/tariffUtils";
import { getDateString } from "@/utils/translationHelpers";

export const FlatMobileCostDetails = ({
  simDetail,
  showSubsequentCosts,
}: {
  simDetail: SimCardDetail;
  showSubsequentCosts: boolean;
}) => {
  const { t } = useTranslation();
  const price = simDetail.priceBase;
  const priceTotal = simDetail.priceDiscounted || 0;

  const oneTimeDiscounts = calculateDiscounts(simDetail.localDiscounts);
  const recurringDiscounts = calculateDiscounts(
    simDetail.successorForRenew?.successorDiscounts,
  );

  if (!price) return null;

  const payInterval =
    simDetail.minContractDuration === 30
      ? t("portal:cockpit.managementTile.tariff.payInterval", { days: 30 })
      : t("portal:cockpit.managementTile.tariff.payInterval", { days: 365 });

  return (
    <div>
      <TariffHeader
        className="mb-6"
        title={t("portal:cockpit.managementTile.tariff.currentCosts")}
      />
      <SimPricingDetails
        productName={getBaseFlatName(simDetail)}
        basePrice={price}
        discounts={oneTimeDiscounts}
        voicePrice={simDetail.priceVoice}
        watchSimPrice={simDetail.priceWatch}
        totalPrice={priceTotal}
        includedFeatures={getIncludedFeaturesCostDetail(
          t,
          getTariffType(simDetail.serviceID),
          simDetail.minContractDuration || 30,
        )}
      />
      <div className="text-right text-base" data-testid="pay-interval-label">
        {payInterval}
      </div>

      {showSubsequentCosts && (
        <SimDetailsAccordion
          content={
            <>
              <TariffHeader
                className={clsx(showSubsequentCosts ? "mb-2" : "mb-6")}
                title={t("cockpit.managementTile.tariff.subsequentCosts")}
              />
              <p className="mb-6">
                {typeof simDetail.remainingDays === "number"
                  ? `${t("cockpit.managementTile.tariff.starting")} ${getDateString(
                      getNextPaymentDate(simDetail.remainingDays + 1),
                    )}`
                  : ""}
              </p>

              <SimPricingDetails
                productName={
                  simDetail.successorForRenew?.description?.replace(
                    / [|+] .*/,
                    "",
                  ) || ""
                }
                basePrice={simDetail.successorForRenew?.priceBase || 0}
                discounts={recurringDiscounts}
                voicePrice={simDetail.successorForRenew?.priceVoice}
                watchSimPrice={simDetail.successorForRenew?.priceWatch}
                totalPrice={simDetail.successorForRenew?.priceDiscounted || 0}
                includedFeatures={getIncludedFeaturesCostDetail(
                  t,
                  getTariffType(simDetail.successorForRenew?.id),
                  simDetail.successorForRenew?.minContractDuration || 30,
                )}
              />

              <div
                className="text-right text-base"
                data-testid="pay-interval-label"
              >
                {t("cockpit.managementTile.tariff.payInterval", {
                  days: simDetail.successorForRenew?.minContractDuration || 30,
                })}
              </div>
            </>
          }
          textOpen={t(
            "portal:cockpit.managementTile.tariff.showSubsequentCosts",
          )}
          textClose={t(
            "portal:cockpit.managementTile.tariff.hideSubsequentCosts",
          )}
        />
      )}
    </div>
  );
};
